// B4-ITEM-TEMPLATES (universal task plan §a "Task templates"): a board's item templates in the
// web app. "Save as template…" (a task's ⋯ menu and its row menu) saves the task's shape — the
// dialog asks what goes with it; "New task" offers the board's templates (picking one fills the
// form, and the server fills the rest: description, checklist, fields, tags, due date); the board
// settings "Templates" tab (board-settings/templates-tab.tsx) lists, edits and archives them.
import type { OperationOutput } from "@companyos/sdk";
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  InfoTip,
  Input,
  Label,
  Notice,
  toast,
} from "@companyos/ui";
import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useId, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { problemText } from "../settings/problem-text";
import { workKeys } from "./work-queries";

export type ItemTemplate = OperationOutput<"itemTemplates.list">["data"][number];

export const itemTemplateKeys = {
  list: (co: string, board: string) => [...workKeys.board(co, board), "item-templates"] as const,
};

export const itemTemplatesQuery = (api: Api, co: string, board: string) =>
  queryOptions({
    queryKey: itemTemplateKeys.list(co, board),
    queryFn: () => api.client.itemTemplates.list({ params: { co, id: board } }),
    staleTime: 30_000,
  });

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? "" : "s"}`;

/** What a template fills besides title, status and priority, in a few words. */
export function templateSummary(t: ItemTemplate): string {
  const p = t.payload;
  const fields = Object.keys(p.fields ?? {}).length;
  return [
    p.body?.length ? "description" : null,
    p.checklist?.length ? plural(p.checklist.length, "checklist item") : null,
    fields ? plural(fields, "field") : null,
    p.tag_ids?.length ? plural(p.tag_ids.length, "tag") : null,
    p.assignee_seat_ids?.length ? "assignees" : null,
    p.due_in_days !== undefined ? `due in ${p.due_in_days}d` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

function Option({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (on: boolean) => void;
}) {
  const id = useId();
  return (
    <div className="flex items-center gap-2">
      <Checkbox id={id} checked={checked} onCheckedChange={(v) => onChange(v === true)} />
      <Label htmlFor={id} className="font-normal">
        {label}
      </Label>
    </div>
  );
}

/** "Save as template…": the task's shape saved on its board, with what goes with it. */
export function SaveAsTemplateDialog({
  api,
  company,
  task,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  task: { id: string; title: string; board_id: string };
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const co = company.id;
  const nameId = useId();
  const [name, setName] = useState(task.title.slice(0, 120));
  const [inc, setInc] = useState({
    description: true,
    checklists: true,
    fields: true,
    tags: true,
    dates: true,
    assignees: false,
  });
  const set = (k: keyof typeof inc) => (on: boolean) => setInc((x) => ({ ...x, [k]: on }));
  const save = useMutation({
    mutationFn: () =>
      api.client.itemTemplates.create({
        params: { co, id: task.board_id },
        body: { name: name.trim(), from_task_id: task.id, include: inc },
      }),
    onSuccess: async (t) => {
      toast.add({ title: `Template “${t.name}” saved`, type: "success" });
      await qc.invalidateQueries({ queryKey: itemTemplateKeys.list(co, task.board_id) });
      onClose();
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim()) save.mutate();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !save.isPending && onClose()}>
      <DialogContent className="sm:max-w-md" data-slot="save-template-dialog">
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-1">
              Save as template
              <InfoTip label="About templates">
                New tasks on this board can start from it: pick it in New task.
              </InfoTip>
            </DialogTitle>
            <DialogDescription>From “{task.title}”.</DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor={nameId}>Name</FieldLabel>
            <Input
              id={nameId}
              required
              maxLength={120}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <fieldset className="grid grid-cols-2 gap-2.5" aria-label="Take with it">
            <Option label="Description" checked={inc.description} onChange={set("description")} />
            <Option label="Checklists" checked={inc.checklists} onChange={set("checklists")} />
            <Option label="Fields" checked={inc.fields} onChange={set("fields")} />
            <Option label="Tags" checked={inc.tags} onChange={set("tags")} />
            <Option label="Due and start" checked={inc.dates} onChange={set("dates")} />
            <Option label="Assignees" checked={inc.assignees} onChange={set("assignees")} />
          </fieldset>
          {save.isError ? (
            <Notice tone="danger" role="alert">
              {problemText(save.error, "The template was not saved.")}
            </Notice>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending || !name.trim()}>
              {save.isPending ? "Saving…" : "Save template"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
